import { useEffect, useMemo, useState } from 'react';
import {
  Button, DatePicker, Form, Input, InputNumber, message, Modal, Popconfirm, Select, Space,
  Switch, Table, Tag, TreeSelect, Typography,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import dayjs, { Dayjs } from 'dayjs';
import { AnnouncementPopup } from '@annea/announcements-ui';
import { api } from '../lib/api';
import type { App } from '../types';

type AnnouncementType = 'NEWS' | 'SERVICE_UPDATE' | 'AD' | 'LIFECYCLE';
type AnnouncementStatus = 'DRAFT' | 'PUBLISHED' | 'ARCHIVED';
type AppTargetMode = 'ALL' | 'FREE' | 'SELECTED';
type Audience = 'ALL' | 'ANONYMOUS' | 'ANNEA' | 'SUBSCRIBER' | 'TRIAL';

interface Announcement {
  id: string;
  type: AnnouncementType;
  status: AnnouncementStatus;
  title: string;
  contentHtml: string;
  validFrom: string;
  validUntil: string | null;
  targetApo: boolean;
  appTargetMode: AppTargetMode;
  audiences: Audience[];
  repeat: boolean;
  repeatIntervalHours: number;
  lifecycleTrigger: 'TRIAL_ENDING' | 'SUBSCRIPTION_ENDING' | null;
  windowStartDaysBefore: number | null;
  windowEndDaysBefore: number | null;
  displayVersion: number;
  updatedAt: string;
  targetApps: Array<{ appId: string; app: App }>;
  excludedApps: Array<{ appId: string; app: App }>;
}

interface FormValues {
  type: AnnouncementType;
  status: AnnouncementStatus;
  title: string;
  contentHtml: string;
  validFrom: Dayjs;
  validUntil?: Dayjs | null;
  targetApo: boolean;
  appTargetMode: AppTargetMode;
  targetAppIds?: string[];
  excludedAppIds?: string[];
  audiences: Audience[];
  repeat: boolean;
  repeatIntervalHours: number;
  lifecycleTrigger?: 'TRIAL_ENDING' | 'SUBSCRIPTION_ENDING' | null;
  windowStartDaysBefore?: number | null;
  windowEndDaysBefore?: number | null;
}

const TYPE_COLORS: Record<AnnouncementType, string> = {
  SERVICE_UPDATE: 'volcano', NEWS: 'blue', AD: 'orange',
  LIFECYCLE: 'purple',
};

function lifecyclePreviewText(value: string) {
  const sample: Record<string, string> = {
    appName: 'Example Suite', suiteName: 'Example Suite',
    endDate: dayjs().add(7, 'day').format('YYYY-MM-DD'), daysRemaining: '7',
    subscriptionUrl: `${window.location.origin}/subscriptions`, userName: 'Example User',
  };
  return value.replace(/\{\{(appName|suiteName|endDate|daysRemaining|subscriptionUrl|userName)\}\}/g, (_match, key) => sample[key] || '');
}

export default function AdminAnnouncementsPage() {
  const [items, setItems] = useState<Announcement[]>([]);
  const [apps, setApps] = useState<App[]>([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<Announcement | null>(null);
  const [preview, setPreview] = useState<any>(null);
  const [saving, setSaving] = useState(false);
  const [form] = Form.useForm<FormValues>();
  const targetMode = Form.useWatch('appTargetMode', form);
  const announcementType = Form.useWatch('type', form);
  const repeatEnabled = Form.useWatch('repeat', form);

  const load = async () => {
    setLoading(true);
    try {
      const [announcementData, appData] = await Promise.all([
        api.admin.listAnnouncements({ pageSize: 100 }), api.admin.listApps(),
      ]);
      setItems(announcementData.items || []);
      setApps(appData.apps || []);
    } catch (error: any) {
      message.error(error.message || 'Failed to load announcements');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { void load(); }, []);

  const treeData = useMemo(() => {
    const childrenByParent = new Map<string, App[]>();
    apps.forEach((app) => {
      if (app.parentId) childrenByParent.set(app.parentId, [...(childrenByParent.get(app.parentId) || []), app]);
    });
    return apps.filter((app) => !app.parentId).map((app) => ({
      title: app.name,
      value: app.id,
      key: app.id,
      children: (childrenByParent.get(app.id) || []).map((child) => ({ title: child.name, value: child.id, key: child.id })),
    }));
  }, [apps]);

  const startCreate = () => {
    setEditing(null);
    form.setFieldsValue({
      type: 'NEWS', status: 'DRAFT', title: '', contentHtml: '<p></p>',
      validFrom: dayjs(), validUntil: null, targetApo: true,
      appTargetMode: 'SELECTED', targetAppIds: [], excludedAppIds: [],
      audiences: ['ALL'], repeat: false,
      repeatIntervalHours: 1, lifecycleTrigger: null,
      windowStartDaysBefore: null, windowEndDaysBefore: null,
    });
    setOpen(true);
  };

  const startEdit = (item: Announcement) => {
    setEditing(item);
    form.setFieldsValue({
      ...item,
      validFrom: dayjs(item.validFrom),
      validUntil: item.validUntil ? dayjs(item.validUntil) : null,
      targetAppIds: item.targetApps.map((entry) => entry.appId),
      excludedAppIds: item.excludedApps.map((entry) => entry.appId),
    });
    setOpen(true);
  };

  const payload = (values: FormValues) => ({
    ...values,
    validFrom: values.validFrom.toISOString(),
    validUntil: values.validUntil ? values.validUntil.toISOString() : null,
    targetAppIds: values.targetAppIds || [],
    excludedAppIds: values.excludedAppIds || [],
  });

  const save = async () => {
    try {
      const values = await form.validateFields();
      setSaving(true);
      if (editing) await api.admin.updateAnnouncement(editing.id, payload(values));
      else await api.admin.createAnnouncement(payload(values));
      message.success(editing ? 'Announcement updated' : 'Announcement created');
      setOpen(false);
      await load();
    } catch (error: any) {
      if (error?.errorFields) return;
      message.error(error.message || 'Failed to save announcement');
    } finally {
      setSaving(false);
    }
  };

  const showPreview = async () => {
    try {
      const values = await form.validateFields(['type', 'title', 'contentHtml']);
      const previewValues = values.type === 'LIFECYCLE'
        ? { ...values, title: lifecyclePreviewText(values.title), contentHtml: lifecyclePreviewText(values.contentHtml) }
        : values;
      const sanitized = await api.admin.previewAnnouncement(previewValues as any);
      setPreview({
        id: editing?.id || 'preview', type: sanitized.type, title: sanitized.title,
        contentHtml: sanitized.contentHtml, validFrom: new Date().toISOString(), validUntil: null,
        repeat: false, displayVersion: 1, createdAt: new Date().toISOString(),
      });
    } catch (error: any) {
      if (!error?.errorFields) message.error(error.message || 'Preview failed');
    }
  };

  const showListPreview = async (item: Announcement) => {
    try {
      const previewValues = item.type === 'LIFECYCLE'
        ? { type: item.type, title: lifecyclePreviewText(item.title), contentHtml: lifecyclePreviewText(item.contentHtml) }
        : { type: item.type, title: item.title, contentHtml: item.contentHtml };
      const sanitized = await api.admin.previewAnnouncement(previewValues as any);
      setPreview({
        id: item.id, type: sanitized.type, title: sanitized.title,
        contentHtml: sanitized.contentHtml, validFrom: item.validFrom, validUntil: item.validUntil,
        repeat: item.repeat, repeatIntervalHours: item.repeatIntervalHours,
        displayVersion: item.displayVersion, createdAt: item.validFrom,
      });
    } catch (error: any) {
      message.error(error.message || 'Preview failed');
    }
  };

  const act = async (operation: () => Promise<any>, success: string) => {
    try { await operation(); message.success(success); await load(); }
    catch (error: any) { message.error(error.message || 'Operation failed'); }
  };

  const columns: ColumnsType<Announcement> = [
    { title: 'Type', dataIndex: 'type', render: (value: AnnouncementType) => <Tag color={TYPE_COLORS[value]}>{value.replace('_', ' ')}</Tag> },
    { title: 'Title', dataIndex: 'title' },
    { title: 'Status', dataIndex: 'status', render: (value: AnnouncementStatus) => <Tag color={value === 'PUBLISHED' ? 'green' : value === 'ARCHIVED' ? 'default' : 'gold'}>{value}</Tag> },
    { title: 'Valid from', dataIndex: 'validFrom', render: (value: string) => new Date(value).toLocaleString() },
    { title: 'Valid until', dataIndex: 'validUntil', render: (value: string | null) => value ? new Date(value).toLocaleString() : 'Unlimited' },
    { title: 'Audience', dataIndex: 'audiences', render: (values: Audience[]) => values.map((value) => <Tag key={value}>{value}</Tag>) },
    { title: 'Repeat', dataIndex: 'repeat', render: (value: boolean, item) => value ? `Every ${item.repeatIntervalHours}h` : 'No' },
    {
      title: 'Actions', key: 'actions', width: 390,
      render: (_, item) => (
        <Space wrap>
          <Button size="small" type="primary" ghost onClick={() => void showListPreview(item)}>Preview</Button>
          <Button size="small" onClick={() => startEdit(item)}>Edit</Button>
          <Button size="small" onClick={() => act(() => api.admin.copyAnnouncement(item.id), 'Draft copy created')}>Copy</Button>
          <Popconfirm title="Show this announcement again to users?" onConfirm={() => act(() => api.admin.resetAnnouncementViews(item.id), 'Viewed state reset')}>
            <Button size="small">Reset viewed</Button>
          </Popconfirm>
          {item.status !== 'ARCHIVED' && (
            <Popconfirm title="Archive this announcement?" onConfirm={() => act(() => api.admin.deleteAnnouncement(item.id), 'Announcement archived')}>
              <Button size="small" danger>Archive</Button>
            </Popconfirm>
          )}
        </Space>
      ),
    },
  ];

  return (
    <Space direction="vertical" size="large" style={{ width: '100%' }}>
      <Space style={{ width: '100%', justifyContent: 'space-between' }}>
        <div>
          <Typography.Title level={2} style={{ marginBottom: 0 }}>Announcements</Typography.Title>
          <Typography.Text type="secondary">News, service updates, and internal ads.</Typography.Text>
        </div>
        <Button type="primary" onClick={startCreate}>Add announcement</Button>
      </Space>
      <Table rowKey="id" loading={loading} columns={columns} dataSource={items} scroll={{ x: 1100 }} pagination={{ pageSize: 25 }} />

      <Modal title={editing ? 'Edit announcement' : 'Add announcement'} open={open} width={880} onCancel={() => setOpen(false)}
        footer={<Space><Button onClick={showPreview}>Preview</Button><Button onClick={() => setOpen(false)}>Cancel</Button><Button type="primary" loading={saving} onClick={save}>Save</Button></Space>}>
        <Form form={form} layout="vertical">
          <Space align="start" wrap style={{ width: '100%' }}>
            <Form.Item name="type" label="Type" rules={[{ required: true }]}><Select style={{ width: 200 }} options={[{ value: 'NEWS', label: 'News' }, { value: 'SERVICE_UPDATE', label: 'Service update' }, { value: 'AD', label: 'Ad' }, { value: 'LIFECYCLE', label: 'Subscription lifecycle' }]}
              onChange={(value: AnnouncementType) => {
                if (value === 'LIFECYCLE') form.setFieldsValue({
                  lifecycleTrigger: 'TRIAL_ENDING', windowStartDaysBefore: 7,
                  windowEndDaysBefore: 0, repeat: true, repeatIntervalHours: 24,
                  audiences: ['TRIAL'], title: 'Your trial ends soon',
                  contentHtml: '<p>Your trial for <strong>{{appName}}</strong> ends on {{endDate}}.</p><p>You have {{daysRemaining}} days remaining. <a href="{{subscriptionUrl}}">View subscription options</a>.</p>',
                });
              }} /></Form.Item>
            <Form.Item name="status" label="Status" rules={[{ required: true }]}><Select style={{ width: 180 }} options={[{ value: 'DRAFT', label: 'Draft' }, { value: 'PUBLISHED', label: 'Published' }, { value: 'ARCHIVED', label: 'Archived' }]} /></Form.Item>
            <Form.Item name="repeat" label="Repeat" valuePropName="checked" tooltip="After dismissal, the announcement becomes eligible again when its configured interval has elapsed."><Switch disabled={announcementType === 'LIFECYCLE'} /></Form.Item>
            {repeatEnabled && announcementType !== 'LIFECYCLE' && (
              <Form.Item name="repeatIntervalHours" label="Repeat every (hours)" rules={[{ required: true }]}><InputNumber min={1} max={8760} /></Form.Item>
            )}
            <Form.Item name="targetApo" label="Show in APO" valuePropName="checked"><Switch /></Form.Item>
          </Space>
          {announcementType === 'LIFECYCLE' && (
            <>
              <Space align="start" wrap>
                <Form.Item name="lifecycleTrigger" label="Lifecycle trigger" rules={[{ required: true }]}>
                  <Select style={{ width: 240 }} options={[{ value: 'TRIAL_ENDING', label: 'Trial ending' }, { value: 'SUBSCRIPTION_ENDING', label: 'Subscription ending' }]}
                    onChange={(value) => form.setFieldsValue({
                      windowStartDaysBefore: value === 'TRIAL_ENDING' ? 7 : 14,
                      audiences: [value === 'TRIAL_ENDING' ? 'TRIAL' : 'SUBSCRIBER'],
                    })} />
                </Form.Item>
                <Form.Item name="windowStartDaysBefore" label="Start days before" rules={[{ required: true }]}><InputNumber min={0} max={3650} /></Form.Item>
                <Form.Item name="windowEndDaysBefore" label="End days before" rules={[{ required: true }]}><InputNumber min={0} max={3650} /></Form.Item>
                <Form.Item name="repeatIntervalHours" label="Repeat every (hours)" rules={[{ required: true }]}><InputNumber min={1} max={8760} /></Form.Item>
              </Space>
              <Typography.Paragraph type="secondary">
                Subscription ending applies only to scheduled cancellation or a manually managed subscription with a definite end date—not normal automatic renewal.
              </Typography.Paragraph>
            </>
          )}
          <Form.Item name="title" label="Title" rules={[{ required: true, max: 160 }]}><Input maxLength={160} showCount /></Form.Item>
          <Space align="start" wrap>
            <Form.Item name="validFrom" label="Valid from" rules={[{ required: true }]}><DatePicker showTime /></Form.Item>
            <Form.Item name="validUntil" label="Valid until (optional)"><DatePicker showTime allowClear /></Form.Item>
          </Space>
          <Form.Item name="appTargetMode" label="Application targets" rules={[{ required: true }]}>
            <Select options={[{ value: 'ALL', label: 'All apps' }, { value: 'FREE', label: 'All free apps' }, { value: 'SELECTED', label: 'Selected apps/suites' }]} />
          </Form.Item>
          {targetMode === 'SELECTED' && <Form.Item name="targetAppIds" label="Selected apps and suites"><TreeSelect treeData={treeData} treeCheckable showCheckedStrategy={TreeSelect.SHOW_PARENT} allowClear /></Form.Item>}
          <Form.Item name="excludedAppIds" label="Excluded apps and suites"><TreeSelect treeData={treeData} treeCheckable showCheckedStrategy={TreeSelect.SHOW_PARENT} allowClear /></Form.Item>
          <Form.Item name="audiences" label="Target users" rules={[{ required: true }]}>
            <Select mode="multiple" disabled={announcementType === 'LIFECYCLE'} options={[{ value: 'ALL', label: 'All (including anonymous)' }, { value: 'ANONYMOUS', label: 'Anonymous users only' }, { value: 'ANNEA', label: 'Annea users' }, { value: 'SUBSCRIBER', label: 'Current subscribers' }, { value: 'TRIAL', label: 'Current trial users' }]}
              onSelect={(value: Audience) => {
                if (value === 'ALL' || value === 'ANONYMOUS') {
                  form.setFieldValue('audiences', [value]);
                  return;
                }
                const values = (form.getFieldValue('audiences') || []) as Audience[];
                form.setFieldValue('audiences', values.filter((audience) => audience !== 'ALL' && audience !== 'ANONYMOUS'));
              }} />
          </Form.Item>
          <Form.Item name="contentHtml" label="HTML message" rules={[{ required: true }]} extra={announcementType === 'LIFECYCLE'
            ? 'Variables: {{appName}}, {{suiteName}}, {{endDate}}, {{daysRemaining}}, {{subscriptionUrl}}, {{userName}}. Unsafe HTML is removed.'
            : 'Allowed formatting includes paragraphs, headings, emphasis, lists, links, quotes, and code. Unsafe HTML is removed.'}>
            <Input.TextArea rows={12} maxLength={100000} showCount />
          </Form.Item>
        </Form>
      </Modal>
      {preview && <AnnouncementPopup announcement={preview} onDismiss={() => setPreview(null)} dismissLabel="Close preview" />}
    </Space>
  );
}
